import type { FC } from "react";
import { memo, useEffect } from "react";

import { sendTelemetryEvent } from "@/lib/telemetry";
import ImageLoader, { type IImageLoader } from "@Components/ImageLoader";
import { TelemetryEntity } from "@Components/Modules/Helpers";
import Text from "@Components/Text";
import type { QueryLgMin, QuerySmMaxToSmMin, QueryXlMin, QueryXsMax } from "@stylex/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

const xsMax: QueryXsMax = "@media (max-width: 759px)";
const lgMin: QueryLgMin = "@media (min-width: 1240px)";
const xlMin: QueryXlMin = "@media (min-width: 1600px)";
const smMaxToSmMin: QuerySmMaxToSmMin = "@media (max-width: 989px) and (min-width: 760px)";

const styles = stylex.create({
    appFeatureItemComponent: {
        padding: {
            default: "0 0 0 20px",

            [lgMin]: "0 8vw 0 0",
            [xlMin]: "0 7vw 0 0",
            [xsMax]: "0",
        },

        ":nth-child(2n)": {
            padding: {
                default: "0 20px 0 0",

                [lgMin]: "0 0 0 8vw",
                [xlMin]: "0 0 0 7vw",
                [xsMax]: "0",
            },
            gridTemplateAreas: {
                default: "'title image' 'text image'",
                [xsMax]: "'image' 'title' 'text'",
            },
        },
        gridTemplateAreas: {
            default: "'image title' 'image text'",
            [xsMax]: "'image' 'title' 'text'",
        },
        alignItems: { default: "center", [xsMax]: "unset" },
        display: "grid",
        gridRowGap: "20px",
        gridTemplateColumns: { default: "1fr 1fr", [xsMax]: "unset" },
        justifyContent: { default: "center", [xsMax]: "unset" },
        textAlign: "left",
        height: { default: "440px", [smMaxToSmMin]: "340px", [xsMax]: "auto" },
        marginTop: { default: null, [xsMax]: "50px" },
    },

    image: {
        gridArea: "image",
        alignSelf: "center",
        justifySelf: "center",
        height: { default: "inherit", [xsMax]: "280px" },
    },

    contentPadding: {
        padding: {
            default: "0 0 0 20px",

            [lgMin]: "0 8vw 0 0",
            [xlMin]: "0 7vw 0 0",
            [xsMax]: "0 20px 0 0",
        },
    },

    contentTitle: {
        gridArea: "title",
        alignSelf: "end",
        fontWeight: 300,
    },

    contentText: {
        gridArea: "text",
        alignSelf: "start",
    },
});

export interface IAppFeatureItem {
    id: number;
    title: string;
    description: string;
    telemetryEventType: string;
    itemImage: IImageLoader;
}

export const AppFeatureItem: FC<IAppFeatureItem> = memo((props) => {
    useEffect(() => {
        sendTelemetryEvent({
            eventName: `${TelemetryEntity.Homepage}-${props.telemetryEventType}`,
            actionType: "Display",
            itemId: `${props.id}`,
        });
    }, []);

    return (
        <div {...stylex.props(styles.appFeatureItemComponent)}>
            <ImageLoader styleX={styles.image} {...props.itemImage} lazyLoading />

            <Text
                textElement="p"
                fontStyle="h3"
                fontWeight="300"
                dangerouslySetInnerHTML={{ __html: props.title }}
                styleX={[styles.contentTitle, styles.contentPadding]}
            />

            <Text
                textElement="p"
                fontWeight="400"
                dangerouslySetInnerHTML={{ __html: props.description }}
                styleX={[styles.contentText, styles.contentPadding]}
            />
        </div>
    );
});
